import type { ReactNode } from "react";
import { cloneElement } from "react";

import type { IconComponent } from "@/components/Icon/Icon";
import { Inline } from "@/design-system/Inline";
import { Text } from "@/design-system/Text";

interface TableCellDescriptionProps {
  primary: ReactNode;
  secondary: ReactNode;
  secondaryIcon?: IconComponent;
  secondaryIconTone?: "default" | "brand";
  "data-testid"?: string;
}

/** Truncated primary and supporting text for a leading ticket table cell. */
export function TableCellDescription({
  primary,
  secondary,
  secondaryIcon,
  secondaryIconTone = "default",
  "data-testid": testId,
}: TableCellDescriptionProps): ReactNode {
  return (
    <span className="flex w-full flex-col gap-space-xs overflow-hidden" data-testid={testId}>
      <Text variant="caption-emphasis" align="start" overflow="truncate">
        {primary}
      </Text>
      <Inline as="span" gap="xs" align="center" wrap={false}>
        {secondaryIcon &&
          cloneElement(secondaryIcon, {
            size: 12,
            tone: secondaryIconTone === "brand" ? "brand" : undefined,
          })}
        <Text variant="overline" tone="secondary" overflow="truncate">
          {secondary}
        </Text>
      </Inline>
    </span>
  );
}

interface TableCellCountProps {
  children: ReactNode;
  icon: IconComponent;
  emphasized?: boolean;
}

/** Icon and numeric value for a compact ticket table cell. */
export function TableCellCount({ children, emphasized = false, icon }: TableCellCountProps): ReactNode {
  return (
    <Inline gap="xs" align="center" wrap={false}>
      {cloneElement(icon, {
        appearance: emphasized ? "highlighted" : undefined,
      })}
      <Text variant="caption-emphasis">{children}</Text>
    </Inline>
  );
}
